import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

class MoxieCardQuickAdd {
  constructor(card) {
    this.card = card;
    const dataScript = card.querySelector('[data-moxie-card-variants]');
    if (!dataScript) return;

    try {
      const parsed = JSON.parse(dataScript.textContent);
      this.variants = parsed.variants || [];
    } catch (e) {
      return;
    }
    if (this.variants.length === 0) return;

    this.colorRadios = card.querySelectorAll('.hdt-color-list input[type="radio"]');
    this.sizeList = card.querySelector('.hdt-size-list');
    this.sizeItems = this.sizeList ? this.sizeList.querySelectorAll('span.hdt-size-list-item') : [];
    if (this.sizeItems.length === 0) return;

    this.init();
  }

  init() {
    if (this.colorRadios.length > 0) {
      const firstAvailable = this.variants.find((v) => v.available && v.color);
      if (firstAvailable) {
        const target = Array.from(this.colorRadios).find((r) => r.value === firstAvailable.color);
        if (target) target.checked = true;
      }
      this.updateSizes(this.getSelectedColor());

      this.colorRadios.forEach((radio) => {
        radio.addEventListener('change', () => this.updateSizes(radio.value));
      });
    } else {
      this.updateSizesBySize();
    }

    this.sizeList.addEventListener('click', (event) => {
      const item = event.target.closest('span.hdt-size-list-item');
      if (!item) return;
      const variantId = item.dataset.variantId;
      if (!variantId) return;
      event.preventDefault();
      event.stopPropagation();
      this.addToCart(variantId, item);
    });
  }

  getSelectedColor() {
    const checked = Array.from(this.colorRadios).find((r) => r.checked);
    if (checked) return checked.value;
    const firstEnabled = Array.from(this.colorRadios).find((r) => !r.disabled);
    return firstEnabled ? firstEnabled.value : null;
  }

  updateSizes(color) {
    if (!color) return;
    this.sizeItems.forEach((item) => {
      const size = item.textContent.trim();
      const variant = this.variants.find((v) => v.color === color && v.size === size);
      if (!variant) {
        item.style.display = 'none';
        item.removeAttribute('data-variant-id');
        return;
      }
      item.style.display = '';
      if (variant.available) {
        item.removeAttribute('unavailable');
        item.dataset.variantId = String(variant.id);
      } else {
        item.setAttribute('unavailable', '');
        item.removeAttribute('data-variant-id');
      }
    });
  }

  updateSizesBySize() {
    this.sizeItems.forEach((item) => {
      const size = item.textContent.trim();
      const variant = this.variants.find((v) => v.size === size);
      if (!variant) { item.style.display = 'none'; return; }
      item.style.display = '';
      if (variant.available) {
        item.removeAttribute('unavailable');
        item.dataset.variantId = String(variant.id);
      } else {
        item.setAttribute('unavailable', '');
        item.removeAttribute('data-variant-id');
      }
    });
  }

  async addToCart(variantId, sourceItem) {
    sourceItem.setAttribute('aria-busy', 'true');
    const cartDrawer = document.querySelector('hdt-cart-drawer');
    const cartSectionId = cartDrawer ? cartDrawer.getAttribute('section-id') : null;

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (cartSectionId) {
        formData.append('sections', cartSectionId);
        formData.append('sections_url', window.location.pathname);
      }

      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, config);
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        throw new Error(`cart/add.js ${response.status}: ${data.description || data.message || 'failed'}`);
      }

      if (cartSectionId && data.sections) {
        document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, {
          bubbles: true,
          detail: {
            cartData: data,
            source: 'product-form-component',
            actionAfterATC: 'open_cart_drawer'
          }
        }));
      } else {
        document.dispatchEvent(new CustomEvent(ThemeEvents.cartReload, { bubbles: true }));
      }
    } catch (error) {
      console.error('[Moxie] quick-add fallo:', error);
    } finally {
      sourceItem.removeAttribute('aria-busy');
    }
  }
}

const initCards = (root = document) => {
  root.querySelectorAll('hdt-card-product, .hdt-card-product').forEach((card) => {
    if (card.dataset.moxieQuickAddInit === 'true') return;
    card.dataset.moxieQuickAddInit = 'true';
    new MoxieCardQuickAdd(card);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCards());
} else {
  initCards();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target) initCards(event.target);
});

document.addEventListener('products:update', (event) => {
  const root = event.target && event.target.querySelectorAll ? event.target : document;
  initCards(root);
});
